import { HQ_BASE } from '../constants';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hqStoreService, HqStoreCreate } from '../services/hqStoreService';

const HqStoreCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<HqStoreCreate>({
    name: '',
    region: '',
    area_pyeong: 100,
    voucher_price: 500000,
    franchise_fee: 10000000,
    franchise_status: 'franchisee',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdResult, setCreatedResult] = useState<any | null>(null);
  const [copied, setCopied] = useState(false);

  const update = (k: keyof HqStoreCreate, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const created = await hqStoreService.create(form);
      // 등록 완료 후 즉시 이동하지 않고 모달로 임시 비번 안내
      setCreatedResult(created);
    } catch (err: any) {
      setError(err?.response?.data?.detail || '등록 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate(`${HQ_BASE}/stores`)}
          className="text-slate-500 hover:text-slate-700"
        >
          ← 목록
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">신규 가맹점 등록</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow p-6 space-y-6">
        <Section title="📍 매장 정보">
          <Field label="매장명" required value={form.name} onChange={(v) => update('name', v)} placeholder="예: 강남 1호점" />
          <Field label="지역" value={form.region || ''} onChange={(v) => update('region', v)} placeholder="예: 강남" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="평수" type="number" value={String(form.area_pyeong ?? '')} onChange={(v) => update('area_pyeong', Number(v) || 0)} />
            <Field label="평균 이용권 (원)" type="number" value={String(form.voucher_price ?? '')} onChange={(v) => update('voucher_price', Number(v) || 0)} />
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 leading-relaxed">
            💡 <strong>좌석 수 / 학생 수는 자동 집계됩니다</strong><br/>
            • 좌석 수 = 가맹점이 좌석 배치 등록한 활성 좌석 (실시간)<br/>
            • 학생 수 = 좌석에 현재 배정된 학생 (휴면/이용권 무관)<br/>
            • 본부에서 별도 입력하지 않습니다
          </div>
        </Section>

        <Section title="👤 가맹점주 (자동 owner 계정 생성)">
          <Field label="이름" required value={form.owner_name || ''} onChange={(v) => update('owner_name', v)} />
          <Field label="이메일 (로그인 ID)" type="email" required value={form.owner_email || ''} onChange={(v) => update('owner_email', v)} placeholder="owner@example.com" />
          <Field label="전화번호" required value={form.owner_phone || ''} onChange={(v) => update('owner_phone', v)} placeholder="01012345678" />
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 leading-relaxed">
            🔐 <strong>로그인 안내</strong><br/>
            • <strong>이메일이 기본 로그인 ID</strong>입니다<br/>
            • 임시 비밀번호 <strong>new001</strong> 자동 부여 → 첫 로그인 시 변경 강제<br/>
            • 등록 후 "가맹점 상세" 페이지에서 수정 가능
          </div>
        </Section>

        <Section title="📋 계약">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">유형</label>
              <select
                value={form.franchise_status}
                onChange={(e) => update('franchise_status', e.target.value as any)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="franchisee">가맹점</option>
                <option value="direct">직영점</option>
              </select>
            </div>
            <Field label="계약 시작일" type="date" value={form.contract_started_at || ''} onChange={(v) => update('contract_started_at', v)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="가맹비 (원)" type="number" value={String(form.franchise_fee ?? '')} onChange={(v) => update('franchise_fee', Number(v) || 0)} />
            <Field label="가맹비 납부일" type="date" value={form.franchise_fee_paid_at || ''} onChange={(v) => update('franchise_fee_paid_at', v)} />
          </div>
        </Section>

        <Section title="📝 메모">
          <textarea
            value={form.notes || ''}
            onChange={(e) => update('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border rounded-lg"
            placeholder="특이사항·계약 조건 등"
          />
        </Section>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate(`${HQ_BASE}/stores`)}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={submitting || !form.name}
            className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg disabled:bg-slate-400"
          >
            {submitting ? '등록 중...' : '등록'}
          </button>
        </div>
      </form>

      {/* 등록 완료 — 임시 비번 안내 모달 */}
      {createdResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-4">
              <div className="text-5xl mb-2">🎉</div>
              <h2 className="text-xl font-bold text-slate-800">가맹점 등록 완료</h2>
              <p className="text-sm text-slate-500 mt-1">
                <strong>{createdResult.name}</strong> 가맹점이 생성되었습니다.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold text-amber-800 mb-3">
                ⚠️ 가맹점주에게 아래 로그인 정보를 전달해주세요
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">로그인 이메일</span>
                  <span className="font-mono font-semibold text-slate-900">
                    {createdResult.login_email}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">임시 비밀번호</span>
                  <span className="font-mono font-semibold text-red-600">
                    {createdResult.temp_password || 'new001'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">회원번호</span>
                  <span className="font-mono text-slate-700">{createdResult.user_number}</span>
                </div>
              </div>
              <p className="text-xs text-amber-700 mt-3 leading-relaxed">
                💡 가맹점주가 첫 로그인 시 자동으로 비밀번호 변경 화면으로 이동합니다.
                새 비번 설정 후에 일반 사용 가능합니다.
              </p>
            </div>

            <button
              onClick={() => {
                const text = `[NewAttend 로그인 정보]
가맹점: ${createdResult.name}
로그인 이메일: ${createdResult.login_email}
임시 비밀번호: ${createdResult.temp_password || 'new001'}
접속 주소: https://app.newattend.com/owner/login
※ 첫 로그인 시 비밀번호 변경 필수`;
                copyToClipboard(text);
              }}
              className="w-full px-4 py-2 mb-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold"
            >
              {copied ? '✓ 복사됨' : '📋 정보 한 번에 복사'}
            </button>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setCreatedResult(null);
                  navigate(`${HQ_BASE}/stores`);
                }}
                className="flex-1 px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm"
              >
                목록으로
              </button>
              <button
                onClick={() => {
                  navigate(`${HQ_BASE}/stores/${createdResult.id}`);
                }}
                className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold"
              >
                상세 보기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h2 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
}
const Field: React.FC<FieldProps> = ({ label, value, onChange, type = 'text', required, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
);

export default HqStoreCreatePage;
